/**
 * RiskSummaryBanner Component
 *
 * TurboTax-style aggregate risk display at top of permit detail pages.
 * Shows at-a-glance status of:
 * - Verified items
 * - Items needing review
 * - Blockers that prevent submission
 *
 * Part of Phase 3: Submission Guidance & Review Tracking
 */

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  CheckCircle2,
  AlertTriangle,
  XOctagon,
  ChevronDown,
  ChevronUp,
  ShieldCheck,
  ExternalLink,
  RefreshCw,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState } from 'react';

interface RiskItem {
  id: string;
  title: string;
  description?: string;
  category?: string;
  actionUrl?: string;
  actionLabel?: string;
}

interface RiskSummaryBannerProps {
  verifiedCount: number;
  reviewItems: RiskItem[];
  blockers: RiskItem[];
  onRunComplianceCheck?: () => void;
  isRunningCheck?: boolean;
  lastCheckTime?: string;
  className?: string;
}

type RiskLevel = 'ready' | 'review' | 'blocked';

function getRiskLevel(blockers: RiskItem[], reviewItems: RiskItem[]): RiskLevel {
  if (blockers.length > 0) return 'blocked';
  if (reviewItems.length > 0) return 'review';
  return 'ready';
}

function getRiskStyles(level: RiskLevel) {
  switch (level) {
    case 'ready':
      return {
        border: 'border-green-200 dark:border-green-800',
        bg: 'bg-green-50 dark:bg-green-950/30',
        icon: CheckCircle2,
        iconColor: 'text-green-600 dark:text-green-400',
        title: 'Ready to Submit',
        titleColor: 'text-green-800 dark:text-green-200',
      };
    case 'review':
      return {
        border: 'border-amber-200 dark:border-amber-800',
        bg: 'bg-amber-50 dark:bg-amber-950/30',
        icon: AlertTriangle,
        iconColor: 'text-amber-600 dark:text-amber-400',
        title: 'Review Required',
        titleColor: 'text-amber-800 dark:text-amber-200',
      };
    case 'blocked':
      return {
        border: 'border-red-200 dark:border-red-800',
        bg: 'bg-red-50 dark:bg-red-950/30',
        icon: XOctagon,
        iconColor: 'text-red-600 dark:text-red-400',
        title: 'Cannot Submit',
        titleColor: 'text-red-800 dark:text-red-200',
      };
  }
}

function RiskItemRow({
  item,
  variant,
}: {
  item: RiskItem;
  variant: 'review' | 'blocker';
}) {
  const isBlocker = variant === 'blocker';

  return (
    <div
      className={cn(
        'flex items-start gap-2 py-2 px-3 rounded-md',
        isBlocker
          ? 'bg-red-100/50 dark:bg-red-900/20'
          : 'bg-amber-100/50 dark:bg-amber-900/20'
      )}
    >
      <span
        className={cn(
          'mt-0.5 flex-shrink-0',
          isBlocker ? 'text-red-500' : 'text-amber-500'
        )}
      >
        {isBlocker ? (
          <XOctagon className="h-4 w-4" />
        ) : (
          <AlertTriangle className="h-4 w-4" />
        )}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{item.title}</p>
        {item.description && (
          <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
        )}
        {item.category && (
          <Badge variant="outline" className="mt-1 text-xs">
            {item.category}
          </Badge>
        )}
      </div>
      {item.actionUrl && (
        <Button variant="ghost" size="sm" className="flex-shrink-0" asChild>
          <a href={item.actionUrl} target="_blank" rel="noopener noreferrer">
            {item.actionLabel || 'View'}
            <ExternalLink className="ml-1 h-3 w-3" />
          </a>
        </Button>
      )}
    </div>
  );
}

export function RiskSummaryBanner({
  verifiedCount,
  reviewItems,
  blockers,
  onRunComplianceCheck,
  isRunningCheck,
  lastCheckTime,
  className,
}: RiskSummaryBannerProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const riskLevel = getRiskLevel(blockers, reviewItems);
  const styles = getRiskStyles(riskLevel);
  const Icon = styles.icon;

  const hasIssues = blockers.length > 0 || reviewItems.length > 0;
  const totalIssues = blockers.length + reviewItems.length;

  return (
    <Alert className={cn(styles.border, styles.bg, className)}>
      <div className="flex items-start gap-3">
        <Icon className={cn('h-5 w-5 mt-0.5', styles.iconColor)} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <AlertTitle className={cn('text-base font-semibold', styles.titleColor)}>
              Risk Summary: {styles.title}
            </AlertTitle>
            <div className="flex items-center gap-2">
              {lastCheckTime && (
                <span className="text-xs text-muted-foreground">
                  Last check: {lastCheckTime}
                </span>
              )}
              {onRunComplianceCheck && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRunComplianceCheck}
                  disabled={isRunningCheck}
                  className="h-7"
                >
                  <RefreshCw
                    className={cn('h-3 w-3 mr-1', isRunningCheck && 'animate-spin')}
                  />
                  {isRunningCheck ? 'Checking...' : 'Re-check'}
                </Button>
              )}
            </div>
          </div>

          <AlertDescription className="mt-2">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              <span className="flex items-center gap-1">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <span className="font-medium">{verifiedCount}</span> items verified
              </span>
              {reviewItems.length > 0 && (
                <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="font-medium">{reviewItems.length}</span> items need
                  review
                </span>
              )}
              {blockers.length > 0 && (
                <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
                  <XOctagon className="h-4 w-4" />
                  <span className="font-medium">{blockers.length}</span>{' '}
                  {blockers.length === 1 ? 'blocker' : 'blockers'}
                </span>
              )}
            </div>

            {riskLevel === 'blocked' && blockers.length > 0 && (
              <div className="mt-2 text-sm text-red-700 dark:text-red-300 font-medium">
                You cannot submit this permit until all blockers are resolved.
              </div>
            )}

            {hasIssues && (
              <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="mt-2 h-7 -ml-2">
                    {isExpanded ? (
                      <>
                        <ChevronUp className="h-4 w-4 mr-1" />
                        Hide details
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-4 w-4 mr-1" />
                        Show {totalIssues} {totalIssues === 1 ? 'issue' : 'issues'}
                      </>
                    )}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2 space-y-2">
                  {blockers.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-xs font-semibold text-red-700 dark:text-red-300 uppercase tracking-wider">
                        Blockers (must fix)
                      </h4>
                      {blockers.map((item) => (
                        <RiskItemRow key={item.id} item={item} variant="blocker" />
                      ))}
                    </div>
                  )}
                  {reviewItems.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wider">
                        Needs Review
                      </h4>
                      {reviewItems.map((item) => (
                        <RiskItemRow key={item.id} item={item} variant="review" />
                      ))}
                    </div>
                  )}
                </CollapsibleContent>
              </Collapsible>
            )}

            {riskLevel === 'ready' && (
              <div className="mt-2 flex items-center gap-2 text-green-700 dark:text-green-300">
                <ShieldCheck className="h-4 w-4" />
                <span className="text-sm">
                  All compliance checks passed. This permit is ready for submission.
                </span>
              </div>
            )}
          </AlertDescription>
        </div>
      </div>
    </Alert>
  );
}

/**
 * Build risk items from compliance check findings
 */
export function buildRiskItemsFromFindings(
  findings: Array<{
    ruleCode: string;
    severity: 'critical' | 'warning' | 'info';
    title: string;
    description: string;
    suggestedFix?: string;
  }>
): { blockers: RiskItem[]; reviewItems: RiskItem[] } {
  const blockers: RiskItem[] = [];
  const reviewItems: RiskItem[] = [];

  findings.forEach((finding, index) => {
    const item: RiskItem = {
      id: `${finding.ruleCode}-${index}`,
      title: finding.title,
      description: finding.suggestedFix || finding.description,
      category: finding.ruleCode,
    };

    if (finding.severity === 'critical') {
      blockers.push(item);
    } else if (finding.severity === 'warning') {
      reviewItems.push(item);
    }
    // Info findings are not shown in the banner
  });

  return { blockers, reviewItems };
}

export default RiskSummaryBanner;
